import React, { useState, useEffect, useContext } from 'react';
import { Button, Card, AuthContext, TextInput } from 'components/lib';
import { CheckCircle, ChevronLeft, ChevronRight, ExternalLink, RefreshCw, X, Users, Check } from 'lucide-react';
import axios from 'axios';
import { connectApp } from '../client/connections/appConnectionLogic';
import { createSecureMessageHandler } from '../../utils/securePostMessage';
import apps from '../../constants/apps.json';
import styles from './UnifiedClientWizard.module.css';

// Filtered app sources for import
const IMPORT_SOURCES = apps.filter(app => app.allow_client_import === true);
const DATA_APPS = apps.filter(app => app.type === 'data' && !app.coming_soon);

export function UnifiedClientWizard({ onClose, onComplete, entityTerm = 'Client' }) {
  const authContext = useContext(AuthContext);
  
  // Main wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSource, setSelectedSource] = useState(null); // Can be 'manual' or an import app
  
  // Manual entry state
  const [clientInput, setClientInput] = useState('');
  const [parsedClients, setParsedClients] = useState([]);
  
  // Import state
  const [selectedImportApp, setSelectedImportApp] = useState(null);
  const [importedClients, setImportedClients] = useState([]);
  const [selectedImportedClients, setSelectedImportedClients] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  
  // Shared settings state
  const [monitoringLevel, setMonitoringLevel] = useState('full_monitoring');
  const [dataSource, setDataSource] = useState('qbo');
  const [monitoringLimits, setMonitoringLimits] = useState(null);
  
  // Creation and connection state
  const [creatingClients, setCreatingClients] = useState(false);
  const [createdClients, setCreatedClients] = useState([]);
  const [currentConnectionIndex, setCurrentConnectionIndex] = useState(0);
  const [connectionQueue, setConnectionQueue] = useState([]);
  const [completedConnections, setCompletedConnections] = useState([]);
  const [skippedConnections, setSkippedConnections] = useState([]);

  const entityTermLower = entityTerm.toLowerCase();
  const entityTermPlural = entityTerm === 'Company' ? 'Companies' : 
                          entityTerm === 'Client' ? 'Clients' : 
                          `${entityTerm}s`;

  // State for handling existing bulk connections
  const [existingBulkState, setExistingBulkState] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  // Check for existing bulk state on mount
  useEffect(() => {
    const bulkState = sessionStorage.getItem('bulkConnectionState');
    if (bulkState) {
      try {
        const state = JSON.parse(bulkState);
        // Check if there are still pending connections
        if (state.currentIndex < state.queue.length) {
          setExistingBulkState(state);
          setShowResumeDialog(true);
        } else {
          // All connections are complete, clean up
          sessionStorage.removeItem('bulkConnectionState');
        }
      } catch (error) {
        console.error('Error parsing bulk state:', error);
        sessionStorage.removeItem('bulkConnectionState');
      }
    }
  }, []);

  const handleResumeExisting = () => {
    if (existingBulkState) {
      setConnectionQueue(existingBulkState.queue);
      setCurrentConnectionIndex(existingBulkState.currentIndex);
      setCompletedConnections(existingBulkState.completed);
      setSkippedConnections(existingBulkState.skipped || []);
      setCurrentStep(5); // Jump to connection step
      
      if (existingBulkState.queue.length > 0) {
        setDataSource(existingBulkState.queue[0].appShortName);
      }
    }
    setShowResumeDialog(false);
  };

  const handleStartFresh = () => {
    sessionStorage.removeItem('bulkConnectionState');
    setExistingBulkState(null);
    setShowResumeDialog(false);
    // Start with fresh state
    setCurrentStep(1);
  };

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await axios.get('/api/account/monitoring-limits');
        setMonitoringLimits(response.data);
      } catch (error) {
        console.error('Error fetching monitoring limits:', error);
      }
    };
    fetchLimits();
  }, []);

  // Manual entry functions
  const parseClientInput = (input) => {
    const lines = input.split('\n').map(line => line.trim()).filter(line => line);
    const clients = [];
    
    lines.forEach((line, index) => {
      const parts = line.split(',').map(part => part.trim());
      const name = parts[0];
      const email = parts[1] || '';
      
      if (name) {
        clients.push({
          id: `temp_${index}`,
          name: name,
          email: email,
          status: 'pending'
        });
      }
    });
    
    return clients;
  };

  const handleManualInputChange = (e) => {
    const input = e.target.value;
    setClientInput(input);
    
    if (input.trim()) {
      const parsed = parseClientInput(input);
      setParsedClients(parsed);
    } else {
      setParsedClients([]);
    }
  };

  // Import functions
  const handleImportAppSelect = (app) => {
    setSelectedImportApp(app);
  };

  const handleImportOAuth = () => {
    setIsImporting(true);
    
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    const popup = window.open(
      selectedImportApp.oauthUrl,
      'oauth',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Handle OAuth callback messages - only from trusted origins
    const handleOAuthMessage = (event) => {
      // This handler only called for validated messages from trusted origins
      if (event.data.type === 'oauth_success') {
        setIsImporting(false);
        fetchImportedClients();
        window.removeEventListener('message', secureHandler);
      }
    };

    // Create secure handler with origin validation (Security: Issue #5 fix)
    const secureHandler = createSecureMessageHandler(handleOAuthMessage, {
      expectedMessageType: 'oauth_success'
    });

    window.addEventListener('message', secureHandler);
    
    const checkPopupClosed = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopupClosed);
        setIsImporting(false);
      }
    }, 500);
  };

  const fetchImportedClients = () => {
    setIsImporting(true);
    
    axios.get(selectedImportApp.client_list_api)
      .then(response => {
        setImportedClients(response.data);
        setSelectedImportedClients(response.data.map(client => ({ id: client.id, name: client.name })));
        setIsImporting(false);
      })
      .catch(error => {
        console.error('Error fetching clients:', error);
        setIsImporting(false);
      });
  };

  const toggleImportedClientSelection = (clientId, clientName) => {
    if (selectedImportedClients.some(client => client.id === clientId)) {
      setSelectedImportedClients(selectedImportedClients.filter(client => client.id !== clientId));
    } else {
      setSelectedImportedClients([...selectedImportedClients, { id: clientId, name: clientName }]);
    }
  };

  const toggleAllImportedClients = () => {
    if (selectedImportedClients.length === importedClients.length) {
      setSelectedImportedClients([]);
    } else {
      setSelectedImportedClients(importedClients.map(client => ({ id: client.id, name: client.name })));
    }
  };

  // Shared functions
  const handleNext = () => {
    if (currentStep === 1) {
      // For import sources, handle app selection and auth
      if (selectedSource && selectedSource !== 'manual') {
        const importApp = IMPORT_SOURCES.find(app => app.short_name === selectedSource);
        setSelectedImportApp(importApp);
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (selectedSource !== 'manual' && !importedClients.length) {
        // Stay on step 2 until import is complete
        return;
      }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setCurrentStep(4);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const createAllClients = async () => {
    setCreatingClients(true);
    const created = [];
    
    try {
      const clientsToCreate = selectedSource === 'manual' ? parsedClients : 
                             selectedImportedClients.map(client => ({
                               name: client.name,
                               email: '',
                               status: 'pending'
                             }));
      
      for (const client of clientsToCreate) {
        const accountPayload = {
          name: client.name,
          email: client.email || '',
          active: true,
          account_type: monitoringLevel
        };
        
        const response = await axios.post('/api/account', accountPayload);
        created.push({
          ...client,
          id: response.data.account.id,
          status: 'created'
        });
      }
      
      setCreatedClients(created);
      
      if (dataSource) {
        setConnectionQueue(created.map(client => ({
          ...client,
          appShortName: dataSource,
          connectionStatus: 'pending'
        })));
        setCurrentStep(5);
      } else {
        onComplete(created);
      }
      
    } catch (error) {
      console.error('Error creating clients:', error);
      alert('Error creating some clients. Please try again.');
    }
    setCreatingClients(false);
  };

  // Connection functions
  const connectCurrentClient = () => {
    if (currentConnectionIndex < connectionQueue.length) {
      const client = connectionQueue[currentConnectionIndex];
      
      sessionStorage.setItem('bulkConnectionState', JSON.stringify({
        queue: connectionQueue,
        currentIndex: currentConnectionIndex,
        completed: completedConnections,
        skipped: skippedConnections
      }));
      
      authContext.switchAccount(client.id, `/client/connections?connect=${client.appShortName}&bulk=true`);
    }
  };

  const skipCurrentConnection = () => {
    if (currentConnectionIndex < connectionQueue.length) {
      const client = connectionQueue[currentConnectionIndex];
      setSkippedConnections([...skippedConnections, client]);
      setCurrentConnectionIndex(currentConnectionIndex + 1);
    }
  };

  const isLastConnection = currentConnectionIndex >= connectionQueue.length - 1;
  const currentClient = connectionQueue[currentConnectionIndex];

  const getClientsToProcess = () => {
    return selectedSource === 'manual' ? parsedClients : selectedImportedClients;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className={styles.stepContent}>
            <h2>Choose Your Source</h2>
            <p className={styles.stepDescription}>
              Select how you'd like to add multiple {entityTermPlural.toLowerCase()} to your account.
            </p>
            
            <div className={styles.sourceGrid}>
              {/* Manual Entry Option */}
              <div 
                className={`${styles.sourceCard} ${selectedSource === 'manual' ? styles.selected : ''}`}
                onClick={() => setSelectedSource('manual')}
              >
                <div className={styles.sourceIcon}>
                  ✏️
                </div>
                <div className={styles.sourceName}>Manual Entry</div>
                <div className={styles.sourceDescription}>
                  Type or paste {entityTermLower} names directly
                </div>
                <div className={styles.sourceFeatures}>
                  ✓ Fast and simple<br/>
                  ✓ Copy from spreadsheet<br/>
                  ✓ Include email addresses
                </div>
                {selectedSource === 'manual' && (
                  <CheckCircle className={styles.sourceCheck} size={20} />
                )}
              </div>
              
              {/* Import Sources */}
              {IMPORT_SOURCES.map(app => (
                <div 
                  key={app.id}
                  className={`${styles.sourceCard} ${selectedSource === app.short_name ? styles.selected : ''}`}
                  onClick={() => setSelectedSource(app.short_name)}
                >
                  <div className={styles.sourceIcon}>
                    <img src={app.icon} alt={app.name} className={styles.appIcon} />
                  </div>
                  <div className={styles.sourceName}>{app.name}</div>
                  <div className={styles.sourceDescription}>
                    Import from your {app.name} account
                  </div>
                  <div className={styles.sourceFeatures}>
                    ✓ Import existing lists<br/>
                    ✓ Maintain consistency<br/>
                    ✓ Bulk selection
                  </div>
                  {selectedSource === app.short_name && (
                    <CheckCircle className={styles.sourceCheck} size={20} />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
        
      case 2:
        if (selectedSource === 'manual') {
          return (
            <div className={styles.stepContent}>
              <h2>Enter {entityTermPlural}</h2>
              <p className={styles.stepDescription}>
                Enter {entityTermLower} names, one per line. You can include email addresses separated by commas.
              </p>
              
              <div className={styles.inputSection}>
                <textarea
                  className={styles.clientTextarea}
                  value={clientInput}
                  onChange={handleManualInputChange}
                  placeholder={`Enter ${entityTermLower} names, one per line:

Example:
ABC Company Inc
XYZ Corp, contact@xyzcorp.com
Smith & Associates
Johnson LLC, info@johnson.com`}
                  rows={10}
                />
                
                {parsedClients.length > 0 && (
                  <div className={styles.previewSection}>
                    <h4>Preview ({parsedClients.length} {parsedClients.length === 1 ? entityTermLower : entityTermPlural.toLowerCase()})</h4>
                    <div className={styles.clientPreview}>
                      {parsedClients.map((client, index) => (
                        <div key={index} className={styles.clientPreviewItem}>
                          <strong>{client.name}</strong>
                          {client.email && <span className={styles.clientEmail}>{client.email}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        } else {
          // Import flow - OAuth or client selection
          if (!importedClients.length) {
            return (
              <div className={styles.stepContent}>
                <h2>Connect to {selectedImportApp?.name}</h2>
                <div className={styles.authCard}>
                  <img src={selectedImportApp?.icon} alt={selectedImportApp?.name} className={styles.appIcon} />
                  <h3>{selectedImportApp?.name}</h3>
                  <p>
                    Authorize access to your {selectedImportApp?.name} account to import {entityTermPlural.toLowerCase()}.
                  </p>
                  <Button
                    text={isImporting ? 'Opening authorization...' : `Connect to ${selectedImportApp?.name}`}
                    action={handleImportOAuth}
                    disabled={isImporting}
                    color="primary"
                  />
                  {isImporting && (
                    <div className={styles.authHelper}>
                      A popup window has opened. Complete the authorization there.
                    </div>
                  )}
                </div>
              </div>
            );
          } else {
            return (
              <div className={styles.stepContent}>
                <h2>Select {entityTermPlural} to Import</h2>
                <p className={styles.stepDescription}>
                  Choose which {entityTermPlural.toLowerCase()} from {selectedImportApp?.name} you'd like to import.
                </p>
                
                <div className={styles.selectionSummary}>
                  Selected {selectedImportedClients.length} of {importedClients.length} {entityTermPlural.toLowerCase()}
                </div>
                
                <div className={styles.tableContainer}>
                  <table className={styles.clientTable}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectedImportedClients.length === importedClients.length}
                            onChange={toggleAllImportedClients}
                          />
                        </th>
                        {importedClients.length > 0 && Object.keys(importedClients[0]).map((key) => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importedClients.map((client) => (
                        <tr 
                          key={client.id}
                          className={selectedImportedClients.some(c => c.id === client.id) ? styles.selectedRow : ''}
                          onClick={() => toggleImportedClientSelection(client.id, client.name)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedImportedClients.some(c => c.id === client.id)}
                              onChange={() => {}}
                            />
                          </td>
                          {Object.keys(client).map((key) => (
                            <td key={key}>{client[key]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          }
        }
        
      case 3:
        return (
          <div className={styles.stepContent}>
            <h2>Set Default Options</h2>
            <p className={styles.stepDescription}>
              Choose settings that will apply to all {getClientsToProcess().length} {entityTermPlural.toLowerCase()}.
            </p>
            
            <div className={styles.optionsSection}>
              <div className={styles.optionGroup}>
                <h4>Monitoring Level</h4>
                <div className={styles.radioGroup}>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      value="health_score_only"
                      checked={monitoringLevel === 'health_score_only'}
                      onChange={(e) => setMonitoringLevel(e.target.value)}
                    />
                    <span>Standard Monitoring</span>
                    <small>Health score tracking and basic alerts</small>
                  </label>
                  
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      value="full_monitoring"
                      checked={monitoringLevel === 'full_monitoring'}
                      onChange={(e) => setMonitoringLevel(e.target.value)}
                      disabled={monitoringLimits && !monitoringLimits.canUpgradeToFullMonitoring && getClientsToProcess().length > (3 - monitoringLimits.currentFullyMonitoredCount)}
                    />
                    <span>Premium Monitoring</span>
                    <small>Full analysis, cleanup tools, and custom rules</small>
                  </label>
                </div>
              </div>
              
              <div className={styles.optionGroup}>
                <h4>Data Source Connection</h4>
                <div className={styles.radioGroup}>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      value=""
                      checked={dataSource === ''}
                      onChange={(e) => setDataSource(e.target.value)}
                    />
                    <span>Connect Later</span>
                    <small>Create accounts now, connect data sources individually later</small>
                  </label>
                  
                  {DATA_APPS.map(app => (
                    <label key={app.short_name} className={styles.radioOption}>
                      <input
                        type="radio"
                        value={app.short_name}
                        checked={dataSource === app.short_name}
                        onChange={(e) => setDataSource(e.target.value)}
                      />
                      <span>
                        <img src={app.icon} alt="" className={styles.appIconSmall} />
                        {app.name}
                      </span>
                      <small>Connect all {entityTermPlural.toLowerCase()} to {app.name}</small>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 4:
        const clientsToProcess = getClientsToProcess();
        return (
          <div className={styles.stepContent}>
            <h2>Review & Create</h2>
            <p className={styles.stepDescription}>
              Ready to create {clientsToProcess.length} {clientsToProcess.length === 1 ? entityTermLower : entityTermPlural.toLowerCase()} with the following settings:
            </p>
            
            <div className={styles.reviewSection}>
              <div className={styles.reviewItem}>
                <strong>Import Method:</strong> {selectedSource === 'manual' ? 'Manual Entry' : `Import from ${selectedImportApp?.name}`}
              </div>
              <div className={styles.reviewItem}>
                <strong>Monitoring Level:</strong> {monitoringLevel === 'full_monitoring' ? 'Premium' : 'Standard'}
              </div>
              <div className={styles.reviewItem}>
                <strong>Data Source:</strong> {dataSource ? DATA_APPS.find(a => a.short_name === dataSource)?.name : 'None (connect later)'}
              </div>
              
              <div className={styles.clientSummary}>
                <h4>{entityTermPlural} to Create:</h4>
                <div className={styles.clientGrid}>
                  {clientsToProcess.map((client, index) => (
                    <div key={index} className={styles.clientSummaryItem}>
                      {client.name}
                      {client.email && <small>{client.email}</small>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className={styles.stepContent}>
            <h2>Connection Queue</h2>
            <p className={styles.stepDescription}>
              All {entityTermPlural.toLowerCase()} created successfully! Now let's connect them to {DATA_APPS.find(a => a.short_name === dataSource)?.name}.
            </p>
            
            <div className={styles.connectionQueue}>
              <div className={styles.queueProgress}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill}
                    style={{ width: `${((completedConnections.length + skippedConnections.length) / connectionQueue.length) * 100}%` }}
                  />
                </div>
                <div className={styles.progressText}>
                  {completedConnections.length + skippedConnections.length} of {connectionQueue.length} completed
                </div>
              </div>
              
              {currentClient && (
                <div className={styles.currentConnection}>
                  <h3>Up Next: {currentClient.name}</h3>
                  <p>Connect this {entityTermLower} to {DATA_APPS.find(a => a.short_name === dataSource)?.name}</p>
                  
                  <div className={styles.connectionActions}>
                    <Button 
                      text={`Connect ${currentClient.name}`}
                      action={connectCurrentClient}
                      color="primary"
                    />
                    <Button 
                      text="Skip for Now"
                      action={skipCurrentConnection}
                      color="secondary"
                    />
                  </div>
                </div>
              )}
              
              <div className={styles.queueStatus}>
                {completedConnections.length > 0 && (
                  <div className={styles.statusGroup}>
                    <h4>✅ Connected ({completedConnections.length})</h4>
                    <div className={styles.statusList}>
                      {completedConnections.map(client => (
                        <div key={client.id}>{client.name}</div>
                      ))}
                    </div>
                  </div>
                )}
                
                {skippedConnections.length > 0 && (
                  <div className={styles.statusGroup}>
                    <h4>⏭️ Skipped ({skippedConnections.length})</h4>
                    <div className={styles.statusList}>
                      {skippedConnections.map(client => (
                        <div key={client.id}>{client.name}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const canProceedFromStep1 = () => {
    return selectedSource !== null; // Need a source selected
  };

  const canProceedFromStep2 = () => {
    if (selectedSource === 'manual') {
      return parsedClients.length > 0;
    } else {
      return selectedImportedClients.length > 0;
    }
  };

  // Resume dialog for existing bulk connections
  if (showResumeDialog && existingBulkState) {
    const pendingCount = existingBulkState.queue.length - existingBulkState.currentIndex;
    const completedCount = existingBulkState.completed.length;
    
    return (
      <div className={styles.wizardContainer}>
        <div className={styles.resumeDialog}>
          <h2>Continue Previous Import?</h2>
          <p>
            You have an unfinished bulk connection in progress with <strong>{pendingCount}</strong> {entityTermPlural.toLowerCase()} 
            still waiting to be connected to their data sources.
          </p>
          
          <div className={styles.resumeStats}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{completedCount}</span>
              <span className={styles.statLabel}>Connected</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{pendingCount}</span>
              <span className={styles.statLabel}>Pending</span>
            </div>
          </div>
          
          <div className={styles.resumeActions}>
            <Button 
              text="Continue Where I Left Off"
              action={handleResumeExisting}
              color="primary"
            />
            <Button 
              text="Start Fresh Import"
              action={handleStartFresh}
              color="secondary"
            />
          </div>
          
          <div className={styles.resumeHelp}>
            <small>
              💡 If you continue, you'll go straight to connecting the remaining {entityTermPlural.toLowerCase()}. 
              If you start fresh, your previous progress will be cleared.
            </small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wizardContainer}>
      <div className={styles.wizardHeader}>
        <h1>Add Multiple {entityTermPlural}</h1>
        <button className={styles.closeButton} onClick={onClose}>
          <X size={20} />
        </button>
        
        {/* Progress indicator */}
        <div className={styles.progressSteps}>
          <div className={`${styles.progressStep} ${currentStep >= 1 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepLabel}>Choose Source</div>
          </div>
          <div className={`${styles.progressStep} ${currentStep >= 2 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepLabel}>{selectedSource === 'manual' ? 'Enter' : 'Import'}</div>
          </div>
          <div className={`${styles.progressStep} ${currentStep >= 3 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepLabel}>Settings</div>
          </div>
          <div className={`${styles.progressStep} ${currentStep >= 4 ? styles.active : ''}`}>
            <div className={styles.stepNumber}>4</div>
            <div className={styles.stepLabel}>Review</div>
          </div>
          {dataSource && (
            <div className={`${styles.progressStep} ${currentStep >= 5 ? styles.active : ''}`}>
              <div className={styles.stepNumber}>5</div>
              <div className={styles.stepLabel}>Connect</div>
            </div>
          )}
        </div>
      </div>

      {renderStep()}

      {/* Navigation buttons */}
      <div className={styles.buttonGroup}>
        {currentStep > 1 && currentStep < 5 && (
          <Button 
            text="Back" 
            action={handleBack} 
            color="secondary"
          />
        )}
        
        <div className={styles.rightButtons}>
          <Button 
            text="Cancel" 
            action={onClose} 
            color="secondary"
          />
          
          {currentStep === 1 && (
            <Button 
              text="Next" 
              action={handleNext}
              disabled={!canProceedFromStep1()}
              color="primary"
            />
          )}
          
          {currentStep === 2 && (
            <Button 
              text="Next" 
              action={handleNext} 
              disabled={!canProceedFromStep2()}
              color="primary"
            />
          )}
          
          {currentStep === 3 && (
            <Button 
              text="Next" 
              action={handleNext} 
              color="primary"
            />
          )}
          
          {currentStep === 4 && (
            <Button 
              text={creatingClients ? "Creating..." : `Create ${getClientsToProcess().length} ${getClientsToProcess().length === 1 ? entityTerm : entityTermPlural}`}
              action={createAllClients} 
              disabled={creatingClients}
              color="primary"
            />
          )}
          
          {currentStep === 5 && isLastConnection && (
            <Button 
              text="Finish" 
              action={() => onComplete(createdClients)} 
              color="primary"
            />
          )}
        </div>
      </div>
    </div>
  );
}